import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Input,
  Button,
  Flex,
  FormControl,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Icon,
  useToast,
  InputGroup,
  InputLeftElement,
  Portal,
  useDisclosure,
  FormErrorMessage,
  useBreakpointValue,
} from "@chakra-ui/react";
import { NextSeo } from "next-seo";
import SimpleLayout from "../components/templates/SimpleLayout";
import useRequest from "../utils/useRequest";
import { useTypewriterEffect } from "../hooks/useTypewriterEffect";
import {
  SocialData,
  PaginatedListingsResponse,
  MarketplacePreview,
  ListingsQueriesResponse,
  ListingsLomaResponse,
  ErrorResponse,
  ListingsFallbackResponse,
  MarketplaceDatas,
  EmailVerificationResponse,
  ApifyOverload,
  Listing,
  UserErrorResponse,
} from "../types/types";
import { motion } from "framer-motion";
import {
  FaChevronDown,
  FaDiscord,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaWhatsapp,
  FaRegEnvelope,
} from "react-icons/fa";
import { FaSquareXTwitter } from "react-icons/fa6";
import { MdGroups } from "react-icons/md";
import { IconType } from "react-icons";
import { useRouter } from "next/router";
import Head from "next/head";
import "react-device-frameset/styles/marvel-devices.min.css";
import { MarketplaceIdeas } from "../components/MarketplaceIdeas";
import { MarketplacePreview as MarketplacePreviewComponent } from "../components/MarketplacePreview";
import { UnauthorizedView } from "../components/system/UnauthorizedView";
import LoadingExperience from "../components/LoadingExperience";
import * as Sentry from "@sentry/nextjs";
import LaunchModal from "../components/LaunchModal";
import { LOMA_DARK_TEAL, LOMA_GREEN, LOMA_GREEN_HOVER, LOMA_LANDING_DARK_TEAL, LOMA_LIGHT_TEAL } from "../constants/colors";
import { analytics } from "../utils/analytics";
import ExampleCarousels from "../components/ExampleCarousels";
import ReferralBanner from "../components/ReferralBanner";
import ComparisonTable from "../components/ComparisonTable";
import BackgroundHills from "../components/BackgroundHills";
import Header from "../components/Header";
import EmojiList from "../components/EmojiList";
import { HeroSection } from "../components/HeroSection";
import DailyInputReached from "../components/DailyInputReached";
import { ProcessingState } from "../components/ProcessingState";
import { ReadyToBuild } from "../components/ReadyToBuild";
import SocialAnalysisLoader from "../components/SocialAnalysisLoader";
import posthog from "posthog-js";


const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

type NetworkIconType = {
  [key: string]: IconType;
};

const networkIcons: NetworkIconType = {
  instagram: FaInstagram,
  youtube: FaYoutube,
  twitter: FaSquareXTwitter,
  facebook_group: MdGroups,
  tiktok: FaTiktok,
  discord: FaDiscord,
  whatsapp: FaWhatsapp,
};

const networkDisplayNames: { [key: string]: string } = {
  instagram: "Instagram",
  youtube: "YouTube",
  facebook_group: "Facebook Group",
  tiktok: "TikTok",
  twitter: "X",
  discord: "Coming Soon",
  whatsapp: "Coming Soon",
};

const audienceExamples = [
  "abandoned building urban explorers",
  "anime-style digital illustrators",
  "Arduino robotics hobbyists",
  "backyard birdwatchers in Georgia",
  "Civil War reenactment groups in Virginia",
  "classic Star Wars memorabilia collectors",
  "DIY upcycling crafters",
  "Vintage Nike Air Max fans",
  "dog owners in NYC",
  "fitness enthusiasts in Venice Beach",
  "homeschooling parents in Phoenix",
  "houseplant parents and balcony gardeners in New Orleans",
  "Japanese-English language exchange partners",
  "Marvel cosplayers and costume makers",
  "mid-century antique collectors",
  "middle school science teacher support groups",
  "mountain biking trail clubs in Boulder, CO",
  "N scale model train enthusiasts",
  "Python open source contributors",
  "retro handheld gaming enthusiasts",
  "Scandinavian minimalist living advocates",
  "small-batch homebrewing enthusiasts",
  "street artists in Minneapolis",
  "sustainable fashion advocates in Brooklyn",
  "twin toddler parents forum",
  "vegan food bloggers in Florida",
  "vintage Telecaster guitar collectors",
  "white hat ethical hacking groups",
  "WWII history buffs on Reddit",
  "yoga instructors for seniors",
  "zero waste households in Seattle",
]

const EMAIL_REGEX_PATTERN = /^[\w-\.+\-]+@([\w-]+\.)+[\w-]{2,4}$/g;

const LomaAI = () => {
  const { api } = useRequest();
  const router = useRouter();
  const toast = useToast();
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingByToken, setIsVerifyingByToken] = useState(false);
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const emailSectionRef = useRef<HTMLDivElement>(null);

  const [username, setUsername] = useState("");
  const [network, setNetwork] = useState("");
  const [marketplaceDescription, setMarketplaceDescription] = useState("");
  const [hasSocialInput, setHasSocialInput] = useState(false);
  const [isCustomAudience, setIsCustomAudience] = useState(false);
  const [networkValidationError, setNetworkValidationError] = useState(false);

  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);

  const placeholder = useBreakpointValue({
    base: "@yourhandle or social link",
    md: "@yourhandle or paste your social link"
  });

  const getStartedButtonText = useBreakpointValue({
    base: "Start →",
    md: "Get Started →"
  });

  const [marketplacePreviewDataId, setMarketplacePreviewDataId] = useState<
    string | null
  >(null);
  const [marketplacePreview, setMarketplacePreview] =
    useState<MarketplacePreview | null>(null);
  const [socialData, setSocialData] = useState<SocialData | null>(null);

  const [marketplaceIdeas, setMarketplaceIdeas] = useState<string[] | null>(
    null
  );
  const [customIdea, setCustomIdea] = useState<string | null>(null);
  const [hasSelectedMarketplaceIdea, setHasSelectedMarketplaceIdea] =
    useState(false);
  const [paginatedListings, setPaginatedListings] =
    useState<PaginatedListingsResponse | null>(null);
  const [marketplacePreviewImage, setMarketplacePreviewImage] = useState<
    string | undefined
  >(undefined);
  const [marketplacePreviewImageLoading, setMarketplacePreviewImageLoading] =
    useState(true);
  const [listingPromoPreviewImages, setListingPromoPreviewImages] = useState<
    string[] | undefined
  >(undefined);
  const [
    listingPromoPreviewImagesLoading,
    setListingPromoPreviewImagesLoading,
  ] = useState(false);
  const [sellerPreviewImage, setSellerPreviewImage] = useState<
    string | undefined
  >(undefined);
  const [sellerPreviewImageLoading, setSellerPreviewImageLoading] =
    useState(false);
  const [payoutsPreviewImage, setPayoutsPreviewImage] = useState<
    string | undefined
  >(undefined);
  const [payoutsPreviewImageLoading, setPayoutsPreviewImageLoading] =
    useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [currentStep, setCurrentStep] = useState<string>("");
  const currentStepKeyRef = useRef<string>("");
  const [marketplaceDatas, setMarketplaceDatas] =
    useState<MarketplaceDatas | null>(null);
  const [ideaIndex, setIdeaIndex] = useState<number>(-1);
  const [ideaIndexLookup, setIdeaIndexLookup] = useState<{
    [key: number]: string;
  } | null>(null);
  const [importKey, setImportKey] = useState<string | null>(null);
  const [jobStartTime, setJobStartTime] = useState<number>(0);
  const [waitStartTime, setWaitStartTime] = useState<number>(0);
  const [listingsStartTime, setListingsStartTime] = useState<number>(0);
  const waitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const TIMEOUT_DURATION = 180000; // 180 seconds
  const [stepStartTime, setStepStartTime] = useState<number>(0);
  const [lastSuccessfulPoll, setLastSuccessfulPoll] = useState<number>(0);
  const [pollRetryCount, setPollRetryCount] = useState<number>(0);
  const stepTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingSessionRef = useRef<number>(0);
  
  // Dynamic timeout durations for each step (max 60 seconds per step)
  const STEP_TIMEOUTS = {
    social_data_json: 60000,              // 60 seconds
    marketplace_datas: 60000,             // 60 seconds
    custom_idea: 60000,                   // 60 seconds
    direct_queries: 60000,                // 60 seconds
    google_queries: 60000,                // 60 seconds
    listings: 60000,                      // 60 seconds
    google_listings: 60000,               // 60 seconds
    craigslist_fallback_listings: 60000,  // 60 seconds
    fallback_listings: 60000,             // 60 seconds
    sorted_listings: 60000,               // 60 seconds
    listings_data: 60000,                 // 60 seconds
    preview_images: 60000,                // 60 seconds
  };

  const getStepTimeout = (step: string): number => {
    return STEP_TIMEOUTS[step as keyof typeof STEP_TIMEOUTS] || 60000; // Default 60 seconds
  };

  const MAX_POLL_RETRIES = 5;
  const [visibleIdeas, setVisibleIdeas] = useState<number[]>([0]);
  const [currentAnimatingIndex, setCurrentAnimatingIndex] = useState(0);
  const [selectedSocialData, setSelectedSocialData] =
    useState<SocialData | null>(null);
  const [runIds, setRunIds] = useState<string[]>([]);
  const [googleRunId, setGoogleRunId] = useState<string | null>(null);
  const [queries, setQueries] = useState<ListingsQueriesResponse | null>(null);
  const [recoverState, setRecoverState] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [emailRunId, setEmailRunId] = useState<number | null>(null);
  const [runsLeft, setRunsLeft] = useState<number>(10);
  const [fromError, setFromError] = useState(false);
  const [isOverloaded, setIsOverloaded] = useState(false);
  const [isPreviewSelected, setIsPreviewSelected] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [fallbackTried, setFallbackTried] = useState(false);
  const [showCustomIdea, setShowCustomIdea] = useState(false);
  const [showFallbackInstagram, setShowFallbackInstagram] = useState(false);

  const [referralBannerState, setReferralBannerState] = useState<{
    show: boolean;
    isValid: boolean;
    inviterEmail?: string;
    error?: string;
  }>({ show: false, isValid: false });

  const { preview_id } = router.query;
  const upgradeRef = useRef(false);

  useEffect(() => {
    if (router.isReady) {
      const { upgrade } = router.query;
      if (upgrade === "true") {
        upgradeRef.current = true;
      }
      const { referral_code } = router.query;
      if (referral_code && typeof referral_code === "string") {
        localStorage.setItem("loma_referral_code", referral_code);

        const { referral_code: _, ...cleanQuery } = router.query;
        router.replace({ pathname: router.pathname, query: cleanQuery }, undefined, { shallow: true });
      }
    }
  }, [router.isReady, router.query]);

  useEffect(() => {
    const validateReferralCode = async () => {
      const storedReferralCode = localStorage.getItem("loma_referral_code");
      if (storedReferralCode && !referralBannerState.show && api) {
        try {
          const result = await api.referral.validateReferralCode(storedReferralCode);
          setReferralBannerState({
            show: true,
            isValid: result.valid,
            inviterEmail: result.referrer_email,
            error: result.error
          });
        } catch (error) {
          console.error("Failed to validate referral code:", error);
          setReferralBannerState({
            show: true,
            isValid: false,
            error: "Unable to validate referral code"
          });
        }
      }
    };

    validateReferralCode();
  }, [api, referralBannerState.show]);

  const isDone =
    (Boolean(hasSelectedMarketplaceIdea) &&
      Boolean(marketplacePreview) &&
      Boolean(paginatedListings) &&
      Boolean(socialData)) ||
    preview_id;
  const isDoneAndShowing =
    isDone &&
    !marketplacePreviewImageLoading &&
    !sellerPreviewImageLoading &&
    !payoutsPreviewImageLoading &&
    !listingPromoPreviewImagesLoading;

  const customIdeaRef = useRef(customIdea);
  useEffect(() => {
    customIdeaRef.current = customIdea;
  }, [customIdea]);

  const frontendError = async () => {
    const response = await api.ai.statusChange({
      status: "frontend_error",
      email_run_id: emailRunId,
      email: email,
    });
    if (response.success) {
      setEmailRunId(response.email_run_id);
    }
  }

  const checkLongWait = async () => {
    // If waitStartTime is 0, we should not calculate duration
    if (waitStartTime === 0) {
      return;
    }
    const waitDuration = performance.now() - waitStartTime;
    if (waitDuration > TIMEOUT_DURATION) {
      await api.ai.statusChange({
        status: "long_wait",
        email_run_id: emailRunId,
      });
      // stop tracking wait time for this interaction
      setWaitStartTime(0);
    } else {
      waitTimeoutRef.current = setTimeout(() => {
        checkLongWait();
      }, 1000);
    }
  };

  const { placeholder: typewriterPlaceholder } = useTypewriterEffect({
    examples: audienceExamples,
    isActive: isCustomAudience,
    setCurrentIndex: setCurrentExampleIndex,
  });

  useEffect(() => {
    if (waitStartTime === 0 && waitTimeoutRef.current) {
      clearTimeout(waitTimeoutRef.current);
      waitTimeoutRef.current = null;
    } else if (waitStartTime > 0) {
      waitTimeoutRef.current = setTimeout(() => {
        checkLongWait();
      }, 1000);
    }

    // Cleanup function to clear timeout on unmount
    return () => {
      if (waitTimeoutRef.current) {
        clearTimeout(waitTimeoutRef.current);
        waitTimeoutRef.current = null;
      }
    };
  }, [waitStartTime]);

  const handleStepTimeout = async () => {
    // When marketplace_datas times out but ideas are visible and user hasn't selected yet,
    // silently stop — the user is still deciding. We'll regenerate on-demand if needed.
    if (importKey === "marketplace_datas" && marketplaceIdeas && marketplaceIdeas.length > 0 && !hasSelectedMarketplaceIdea) {
      // Clean up polling state but keep ideas visible — no error toast, no state reset
      setStepStartTime(0);
      clearStepTimeout();
      setRunIds([]);
      setImportKey(null);
      setIsProcessing(false);
      return;
    }

    const currentTimeout = getStepTimeout(importKey || '');
    const currentTimeoutSeconds = Math.round(currentTimeout / 1000);

    // Step timeouts are sent to Sentry
    Sentry.captureException(
      new Error(`LomaAI step timeout - polling exceeded ${currentTimeoutSeconds} seconds`),
      {
        extra: {
          email,
          network,
          username,
          marketplaceDescription,
          importKey,
          runIds,
          stepDuration: performance.now() - stepStartTime,
          timeoutDuration: currentTimeout,
          emailRunId,
          currentStep
        },
        tags: {
          logger_type: "loma-ai",
          feature: "loma-ai", 
          action: "step-timeout",
          error_type: "step_timeout",
        },
      }
    );

    posthog.capture("step_timeout", {
      loma_ai_event: true,
      email,
      network,
      username,
      marketplaceDescription,
      importKey,
      runIds,
      emailRunId,
      stepDuration: performance.now() - stepStartTime,
      timeoutDuration: currentTimeout,
      currentStep,
    });

    // Clean up and restart
    setStepStartTime(0);
    clearStepTimeout();
    recoverError("step_timeout");
    restartMarketplaceCreation();
  };

  const handleStalledRequest = async () => {
    // Log stalled request error to Sentry
    Sentry.captureException(
      new Error(`LomaAI stalled request - network issue detected`),
      {
        extra: {
          email,
          network,
          username,
          marketplaceDescription,
          importKey,
          runIds,
          lastSuccessfulPoll,
          pollRetryCount,
          timeSinceLastPoll: performance.now() - lastSuccessfulPoll,
          emailRunId,
        },
        tags: {
          logger_type: "loma-ai",
          feature: "loma-ai",
          action: "stalled-request",
          error_type: "network_stall",
        },
      }
    );

    // Clean up and restart
    setStepStartTime(0);
    setLastSuccessfulPoll(0);
    setPollRetryCount(0);
    clearStepTimeout();
    recoverError("network_stall");
    restartMarketplaceCreation();
  };

  const clearStepTimeout = () => {
    if (stepTimeoutRef.current) {
      clearTimeout(stepTimeoutRef.current);
      stepTimeoutRef.current = null;
    }

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    pollingSessionRef.current += 1;
  };

  const startStepTimeout = () => {
    clearStepTimeout();
    setStepStartTime(performance.now());
    setLastSuccessfulPoll(performance.now());
    setPollRetryCount(0);
    
    pollingSessionRef.current += 1;
    
    const timeoutDuration = getStepTimeout(importKey || '');
    stepTimeoutRef.current = setTimeout(() => {
      handleStepTimeout();
    }, timeoutDuration);
  };

  const checkForStalledRequest = async () => {
    const timeSinceLastPoll = performance.now() - lastSuccessfulPoll;
    
    if (timeSinceLastPoll > 30000 && pollRetryCount < MAX_POLL_RETRIES) {
      setPollRetryCount(prev => prev + 1);
      setLastSuccessfulPoll(performance.now());
      
      pollingTimeoutRef.current = setTimeout(() => {
        fetchImportResults();
      }, 2000);
      return true;
    }
    
    if (pollRetryCount >= MAX_POLL_RETRIES) {
      await handleStalledRequest();
      return true;
    }
    
    return false;
  };

  // Clean up step timeout on unmount
  useEffect(() => {
    return () => {
      clearStepTimeout();
    };
  }, []);

  // Reset step timeout when importKey changes (new step starts)
  useEffect(() => {
    if (importKey && runIds.length > 0) {
      startStepTimeout();
    } else {
      clearStepTimeout();
      setStepStartTime(0);
      setLastSuccessfulPoll(0);
      setPollRetryCount(0);
    }
  }, [importKey, runIds]);

  const fetchImportResults = useCallback(async () => {
    const currentSessionId = pollingSessionRef.current;
    
    if (runIds.length === 0 || !importKey) return;

    if (customIdeaRef.current && importKey === "marketplace_datas") {
      console.log(
        "Aborting marketplace_datas import due to custom idea being set (via ref)"
      );
      setRunIds([]);
      setImportKey(null);
      setMarketplaceDatas(null);
      setIsProcessing(false);
      clearStepTimeout();
      return;
    }

    setIsProcessing(true);
    console.log("fetchImportResults", runIds, importKey);
    let results;
    try {
      results = await api.ai.getImportLogResults(runIds, importKey);
      
      if (currentSessionId !== pollingSessionRef.current) {
        console.log("Aborting fetchImportResults - session changed during API call");
        return;
      }
      
      setLastSuccessfulPoll(performance.now());
      setPollRetryCount(0);
    } catch (error) {
      console.error("Error in fetchImportResults:", error);
      
      if (currentSessionId !== pollingSessionRef.current) {
        console.log("Aborting fetchImportResults - session changed during error handling");
        return;
      }
      
      posthog.capture("fetch_import_results_error", {
        loma_ai_event: true,
        email,
        network,
        username,
        marketplaceDescription,
        importKey,
        runIds,
        emailRunId,
        sessionId: currentSessionId,
        pollRetryCount,
        timeSinceLastPoll: performance.now() - lastSuccessfulPoll,
      });

      Sentry.captureException(error instanceof Error ? error : new Error("fetchImportResults error"), {
        extra: {
          email,
          network,
          username,
          marketplaceDescription,
          importKey,
          runIds,
          emailRunId,
          sessionId: currentSessionId,
          pollRetryCount,
          timeSinceLastPoll: performance.now() - lastSuccessfulPoll,
        },
        tags: {
          logger_type: "loma-ai",
          feature: "loma-ai",
          action: "fetch-import-results-error",
        },
      });
      
      const isStalledAndHandled = await checkForStalledRequest();
      if (isStalledAndHandled) {
        return;
      }
      
      pollingTimeoutRef.current = setTimeout(() => {
        fetchImportResults();
      }, 2000);
      return;
    }

    if (results.results === null) {
      if (currentSessionId !== pollingSessionRef.current) {
        console.log("Aborting fetchImportResults - session changed during null results check");
        return;
      }
      
      // Check for step timeout
      const currentStepTimeout = getStepTimeout(importKey || '');
      if (stepStartTime > 0 && performance.now() - stepStartTime > currentStepTimeout) {
        await handleStepTimeout();
        return;
      }
      
      if (performance.now() - jobStartTime > TIMEOUT_DURATION) {
        // log the timeout
        await api.ai.statusChange({
          status: "timeout",
          email_run_id: emailRunId,
          run_ids: runIds,
        });
        recoverError("timeout");
        restartMarketplaceCreation();
        return;
      }
      // Use tracked timeout instead of bare setTimeout
      pollingTimeoutRef.current = setTimeout(() => {
        fetchImportResults();
      }, 1000);
      return;
    }

    // Final session check before processing results
    if (currentSessionId !== pollingSessionRef.current) {
      console.log("Aborting fetchImportResults - session changed before processing results");
      return;
    }

    clearStepTimeout(); // This now clears both step and polling timeouts

    if ((results.results as UserErrorResponse)?.user_error) {
      const userError =
        (results.results as UserErrorResponse)?.user_error ?? "";
      setRunIds([]);
      setIsProcessing(false);
      clearStepTimeout();

      if (importKey === "social_data_json") {
        toast({
          title: "Social account not available",
          description: formatErrorWithSupport(userError),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setCurrentStep("");
        currentStepKeyRef.current = "";

        posthog.capture("social_data_user_error", {
          loma_ai_event: true,
          userError,
          email,
          network,
          username,
        });

        return;
      }
      // should not be reachable outside of social_data_json, but fallback treat it as a normal error
      setFromError(true); // keep their email run
      toast({
        title: "Hmm... Something went wrong",
        description: formatErrorWithSupport(
          "We weren't able to create your marketplace at this time. Please try again."
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      posthog.capture("user_error", {
        loma_ai_event: true,
        email,
        network,
        username,
      });
      restartMarketplaceCreation();
      return;
    }

    if ((results.results as ErrorResponse)?.error) {
      // backend error
      const backendError = (results.results as ErrorResponse)?.error ?? "";
      console.error(backendError);
      setRunIds([]);
      setIsProcessing(false);
      clearStepTimeout();

      if (backendError.includes("OpenAI system error")) {
        posthog.capture("openai_system_error", {
          loma_ai_event: true,
          email,
          network,
          username,
          marketplaceDescription,
          importKey,
          runIds,
          backendError,
          emailRunId,
        });

        Sentry.captureException(new Error("OpenAI system error"), {
          extra: {
            email,
            network,
            username,
            marketplaceDescription,
            importKey,
            runIds,
            backendError,
            emailRunId,
          },
          tags: {
            logger_type: "loma-ai",
            feature: "loma-ai",
            action: "openai-system-error",
          },
        });
        
        toast({
          title: "We are currently experiencing system outages.",
          description: formatErrorWithSupport("Please try again later."),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setCurrentStep("");
        currentStepKeyRef.current = "";
        return;
      }

      if (importKey === "social_data_json") {
        toast({
          title: "Social account not available",
          description: formatErrorWithSupport(backendError),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setCurrentStep("");
        currentStepKeyRef.current = "";

        posthog.capture("social_data_backend_error", {
          loma_ai_event: true,
          backendError,
          email,
          network,
          username,
        });
        
        return;
      }

      if (
        backendError ===
        "Not enough listings found. Please try a less specific niche."
      ) {
        posthog.capture("niche_too_specific", {
          loma_ai_event: true,
          email,
          network,
          username,
          marketplaceDescription,
          importKey,
          backendError,
        });
        
        knownError(backendError, "Too specific to generate");
        restartMarketplaceCreation();
        return;
      }

      posthog.capture("backend_error", {
        loma_ai_event: true,
        email,
        network,
        username,
        marketplaceDescription,
        importKey,
        runIds,
        backendError,
        emailRunId,
        hasListings: !!paginatedListings,
        hasIdeas: !!marketplaceIdeas,
      });

      Sentry.captureException(new Error(`Backend error: ${backendError}`), {
        extra: {
          email,
          network,
          username,
          marketplaceDescription,
          importKey,
          runIds,
          backendError,
          emailRunId,
          hasListings: !!paginatedListings,
          hasIdeas: !!marketplaceIdeas,
        },
        tags: {
          logger_type: "loma-ai",
          feature: "loma-ai",
          action: "backend-error",
        },
      });

      if (paginatedListings) {
        recoverError("at preview images");
      } else if (marketplaceIdeas) {
        recoverError("with idea");
      } else {
        recoverError(importKey ?? "");
      }
      restartMarketplaceCreation();
      return;
    }
    const currentRunIds = [...runIds];
    setRunIds([]);
    clearStepTimeout();

    console.log(
      importKey + " time:",
      ((performance.now() - jobStartTime) / 1000).toFixed(2) + "s"
    );
    setIsProcessing(false);
    const myImportKey = importKey;
    setImportKey(null);
    if (myImportKey === "social_data_json") {
      const apifyOverload = results.results as ApifyOverload;
      if (apifyOverload.triggered_at) {
        setIsOverloaded(true);
        console.error("Overloaded:", apifyOverload.apify_running_count);
        toast({
          title: "At Capacity",
          description:
            "Our servers are currently overloaded. Please try again later.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });

        posthog.capture("apify_overload", {
          loma_ai_event: true,
          email,
          network,
          username,
          marketplaceDescription,
          importKey,
          runIds,
          emailRunId,
        });

        Sentry.captureException(new Error("Apify overload"), {
          extra: {
            email,
            network,
            username,
          },
          tags: {
            logger_type: "loma-ai",
            feature: "loma-ai",
            action: "apify-overload",
          },
        });
        
        return;
      }
      setIsOverloaded(false);

      const socialDataJson = {
        username: username,
        network: network,
        commerce_modality: "marketplace",
        listing_geography_exclusivity: null,
        fulfillment_modality: ["shippable_small_parcel_goods"],
        user_base_exclusivity: null,
        marketplace_ideas: null,
        account_dimensions: null,
        marketplace_description: marketplaceDescription,
        social_data: null,
      } as SocialData;

      const data = results.results as SocialData;
      Object.keys(data).forEach((key) => {
        if (key in socialDataJson) {
          const typedKey = key as keyof SocialData;
          (socialDataJson[typedKey] as any) = data[typedKey];
        }
      });
      setSocialData(socialDataJson);
      setMarketplaceIdeas(socialDataJson.marketplace_ideas);
      setMarketplaceDescription(socialDataJson.marketplace_description);
      createMarketplaces(socialDataJson);

      posthog.capture("ideas_received", {
        loma_ai_event: true,
        email: email,
        username: username,
        network: network,
        marketplace_ideas: socialDataJson.marketplace_ideas,
        marketplace_description: socialDataJson.marketplace_description,
      });
    } else if (myImportKey === "marketplace_datas") {
      console.log("setting marketplace datas", results.results);
      setMarketplaceDatas(results.results as MarketplaceDatas);
    } else if (myImportKey === "custom_idea") {
      console.log("setting custom marketplace", results.results);
      setMarketplaceDatas(results.results as MarketplaceDatas);
      // Only override ideaIndex for actual custom ideas typed by the user.
      // For regenerated existing ideas, ideaIndex is already correct (0/1/2).
      if (customIdea !== null) {
        setIdeaIndex(3);
      }
    } else if (myImportKey === "direct_queries") {
      const myQueries = results.results as ListingsQueriesResponse;
      if (myQueries.third_party_marketplace === "") {
        console.log("no third party marketplace, using google queries");
        setRunIds([googleRunId as string]);
        setImportKey("google_queries");
        return;
      }
      setQueries(myQueries);
      if (myQueries.third_party_marketplace === "google") {
        setRunIds([googleRunId as string]);
        setImportKey("google_queries");
      } else {
        fetchListings(myQueries);
      }
    } else if (myImportKey === "google_queries") {
      const myQueries = results.results as ListingsQueriesResponse;
      setQueries(myQueries);
      fetchListings(myQueries);
    } else if (
      myImportKey === "listings" ||
      myImportKey === "google_listings" ||
      myImportKey === "craigslist_fallback_listings" ||
      myImportKey === "fallback_listings"
    ) {
      const lomaListingses = results.results as
        | ListingsLomaResponse[]
        | ListingsFallbackResponse[];
      console.log("lomaListingses", lomaListingses);
      const lomaListings = {
        loma_listings: lomaListingses.flatMap(
          (lomaListingResponse) =>
            (lomaListingResponse as ListingsLomaResponse)?.loma_listings ||
            (lomaListingResponse as ListingsFallbackResponse)?.fallback_listings
        ),
      } as ListingsLomaResponse;
      console.log("lomaListings", myImportKey, lomaListings);
      if (lomaListings.loma_listings.length === 0) {
        console.log(
          "no results from lomaListings",
          myImportKey,
          queries?.third_party_marketplace
        );
        /* Fallback logic:
          If no results from CL, try fallback CL queries
          If no results from third party (or fallback CL), try Google queries
          If no results from Google, try fallback Google queries
          If no results from Google fallback queries, restart the process
        */
        if (queries && queries.third_party_marketplace !== "google") {
          console.log("try google queries");
          setRunIds([googleRunId as string]);
          setImportKey("google_queries");
        } else if (
          myImportKey !== "fallback_listings" &&
          !fallbackTried &&
          queries &&
          queries.fallback_search_datas.length > 0
        ) {
          console.log("try google fallback");
          listingsFallback("google", queries.fallback_search_datas);
        } else {
          console.log("no results from Google fallback");
          recoverError("with idea");
          restartMarketplaceCreation();
        }
        return;
      }
      sortLomaListings(lomaListings);
    } else if (myImportKey === "sorted_listings") {
      const sortedListings = results.results as ListingsLomaResponse;
      // if no results after validation, try fallback queries
      if (sortedListings.loma_listings.length === 0) {
        if (
          !fallbackTried &&
          queries?.fallback_search_datas &&
          queries.fallback_search_datas.length > 0
        ) {
          console.log("try google fallback after sorting");
          listingsFallback("google", queries?.fallback_search_datas);
        } else {
          console.log("no results after sorting");
          recoverError("with idea");
          restartMarketplaceCreation();
        }
        return;
      }
      createListingsData(sortedListings);
    } else if (myImportKey === "listings_data") {
      const listingsData = results.results as PaginatedListingsResponse;
      if (!listingsData.results || listingsData.results.length === 0) {
        recoverError("with idea");
        restartMarketplaceCreation();
        return;
      }
      setPaginatedListings(listingsData);
      console.log(
        "TOTAL LISTINGS time:",
        ((performance.now() - listingsStartTime) / 1000).toFixed(2) + "s"
      );
    } else if (myImportKey === "preview_images") {
      const previewImages = results.results as { [key: string]: string[] };
      console.log("previewImages", previewImages);
      const marketplaceImg = previewImages?.[currentRunIds?.[0] ?? 0]?.[0];
      console.log("marketplaceImg", marketplaceImg);
      const sellerImg = previewImages?.[currentRunIds?.[1] ?? 0]?.[0];
      console.log("sellerImg", sellerImg);
      const payoutsImg = previewImages?.[currentRunIds?.[2] ?? 0]?.[0];
      console.log("payoutsImg", payoutsImg);
      const promoImages = previewImages?.[currentRunIds?.[3] ?? 0];
      console.log("promoImages", promoImages);

      if (marketplaceImg && sellerImg && payoutsImg) {
        setMarketplacePreviewImage(marketplaceImg);
        setSellerPreviewImage(sellerImg);
        setPayoutsPreviewImage(payoutsImg);
        setMarketplacePreviewImageLoading(false);
        setSellerPreviewImageLoading(false);
        setPayoutsPreviewImageLoading(false);

        if (promoImages && promoImages.length > 0) {
          setListingPromoPreviewImages(promoImages);
          setListingPromoPreviewImagesLoading(false);
        } else {
          console.log("No promo images, showing fallback instagram story");
          setShowFallbackInstagram(true);
          setListingPromoPreviewImagesLoading(false);
        }
      } else {
        const errorMsg = "Failed to load preview images or listing promo images are empty.";
        console.error(errorMsg);
        
        posthog.capture("preview_images_failed", {
          loma_ai_event: true,
          email,
          network,
          username,
          marketplaceDescription,
          marketplacePreviewDataId,
          emailRunId,
          marketplaceImg: !!marketplaceImg,
          sellerImg: !!sellerImg,
          payoutsImg: !!payoutsImg,
          promoImagesCount: promoImages?.length || 0,
        });

        Sentry.captureException(new Error(errorMsg), {
          extra: {
            email,
            network,
            username,
            marketplaceDescription,
            marketplacePreviewDataId,
            emailRunId,
            marketplaceImg: !!marketplaceImg,
            sellerImg: !!sellerImg,
            payoutsImg: !!payoutsImg,
            promoImagesCount: promoImages?.length || 0,
          },
          tags: {
            logger_type: "loma-ai",
            feature: "loma-ai",
            action: "preview-images-failed",
          },
        });
        
        recoverError("at preview images");
        restartMarketplaceCreation();
        return;
      }
    }
  }, [
    runIds,
    importKey,
    customIdea,
    jobStartTime,
    listingsStartTime,
    username,
    network,
    marketplaceDescription,
    googleRunId,
    queries,
    toast,
    stepStartTime,
    lastSuccessfulPoll,
    pollRetryCount,
    emailRunId,
  ]);

  useEffect(() => {
    if (runIds.length > 0 && importKey) {
      console.log("fetching import results", importKey, runIds);
      fetchImportResults();
    }
  }, [runIds, importKey]);

  useEffect(() => {
    if (runsLeft === 0) {
      toast({
        title: "You've reached your limit",
        description:
          "You hit the maximum number of runs for today. Please come back tomorrow.",
        status: "info",
        duration: 5000,
        isClosable: true,
      });

      posthog.capture("runs_left_0", {
        loma_ai_event: true,
        email: email,
        network: network,
        username: username,
      });
    }
    console.log("runsLeft", runsLeft);
  }, [runsLeft]);

  const formatErrorWithSupport = (errorMessage: string) => (
    <Box>
      <Text mb={2}>{errorMessage}</Text>
      <Text fontSize="sm">
        Please contact{" "}
        <Text 
          as="a" 
          href="mailto:support@loma.ai" 
          textDecoration="underline"
          _hover={{ textDecoration: "underline" }}
        >
          support@loma.ai
        </Text>{" "}
        if the error persists.
      </Text>
    </Box>
  );

  const knownError = (
    error: string,
    errorTitle: string = "Hmm... Something went wrong"
  ) => {
    setFromError(true); // keep their email run
    toast({
      title: errorTitle,
      description: formatErrorWithSupport(error),
      status: "error",
      duration: 6000,
      isClosable: true,
    });
    setIsProcessing(false);
  };

  const recoverError = (restartStage = "") => {
    setFromError(true); // keep their email run
    toast({
      title: "Hmm... Something went wrong",
      description: formatErrorWithSupport(
        "We weren't able to create your marketplace at this time. Please try again."
      ),
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    setIsProcessing(false);
    Sentry.captureException(
      new Error(`LomaAI marketplace creation failed at stage: ${restartStage}`),
      {
        extra: {
          email,
          network,
          username,
          marketplaceDescription,
          restartStage,
        },
        tags: {
          logger_type: "loma-ai",
          feature: "loma-ai",
          action: "recover-error",
        },
      }
    );

    posthog.capture("recover_error", {
      loma_ai_event: true,
      email,
      network,
      username,
      restartStage,
    });
  };

  const createMarketplaces = async (socialDataJson: SocialData) => {
    setPaginatedListings(null);
    setMarketplacePreview(null);
    setMarketplacePreviewDataId(null);
    setMarketplacePreviewImage(undefined);
    setSellerPreviewImage(undefined);
    setPayoutsPreviewImage(undefined);
    setListingPromoPreviewImages(undefined);
    console.log("resetting idea index createMarketplaces");
    setMarketplaceDatas(null);
    setCustomIdea(null);
    setCurrentStep(
      `Creating ${socialData?.commerce_modality ?? "marketplace"} structure`
    );
    currentStepKeyRef.current = "marketplace_datas";
    setJobStartTime(performance.now());
    // separately track how long before the user clicks "Create"
    setWaitStartTime(performance.now());

    const runId1 = await api.ai.createInitialMarketplaceData({
      ideas_index: 0,
      social_data_json: {
        ...socialDataJson,
        marketplace_description: socialDataJson.marketplace_ideas?.[0] ?? "",
      } as SocialData,
      email_run_id: emailRunId,
    });
    const runId2 = await api.ai.createInitialMarketplaceData({
      ideas_index: 1,
      social_data_json: {
        ...socialDataJson,
        marketplace_description: socialDataJson.marketplace_ideas?.[1] ?? "",
      } as SocialData,
      email_run_id: emailRunId,
    });
    const runId3 = await api.ai.createInitialMarketplaceData({
      ideas_index: 2,
      social_data_json: {
        ...socialDataJson,
        marketplace_description: socialDataJson.marketplace_ideas?.[2] ?? "",
      } as SocialData,
      email_run_id: emailRunId,
    });
    setIdeaIndexLookup({
      0: runId1 as string,
      1: runId2 as string,
      2: runId3 as string,
    });
    setRunIds([runId1 as string, runId2 as string, runId3 as string]);
    setImportKey("marketplace_datas");
  };

  const createCustomMarketplace = async (socialDataJson: SocialData) => {
    setWaitStartTime(0); // disable long wait tracking for this interaction
    setJobStartTime(performance.now());
    if (!marketplaceIdeas || !customIdea) {
      console.log(
        "skipping createCustomMarketplace",
        marketplaceIdeas,
        customIdea
      );
      return;
    }
    const runIdCustom = await api.ai.createInitialMarketplaceData({
      ideas_index: 3,
      social_data_json: {
        ...socialDataJson,
        marketplace_description: customIdea ?? "",
      } as SocialData,
      email_run_id: emailRunId,
      import_type: "custom_idea",
    });
    setIdeaIndexLookup({
      ...(ideaIndexLookup ?? {}),
      3: runIdCustom as string,
    });
    setRunIds([runIdCustom as string]);
    setMarketplaceIdeas([
      ...(marketplaceIdeas ?? ["", "", ""]),
      customIdea as string,
    ]);
    setImportKey("custom_idea");
  };

  useEffect(() => {
    // trigger the listing step logic as soon as the idea is selected
    if (ideaIndex !== -1 && marketplaceDatas) {
      const ideaRunId = ideaIndexLookup?.[ideaIndex];
      if (ideaRunId && marketplaceDatas[ideaRunId]) {
        setWaitStartTime(0); // disable long wait tracking for this interaction
        setSocialData(marketplaceDatas[ideaRunId].social_data_json);
        setMarketplacePreviewDataId(
          marketplaceDatas[ideaRunId].marketplace_preview_data_id
        );
        setMarketplacePreview(marketplaceDatas[ideaRunId].marketplace_data);
      }
    }
  }, [ideaIndex, marketplaceDatas]);

  useEffect(() => {
    if (socialData && customIdea) {
      setMarketplaceDescription(customIdea);
      setHasSelectedMarketplaceIdea(true);
      createCustomMarketplace(socialData);
      setIdeaIndex(3);
    }
  }, [customIdea]);

  const handleCustomTryAgain = async () => {
    if (!fromError && !isOverloaded) {
      const newEmailRunId = await api.ai.createAIRunId(email);
      if (newEmailRunId.id && newEmailRunId.runs_left) {
        setEmailRunId(newEmailRunId.id);
        setRunsLeft(newEmailRunId.runs_left);
      } else {
        setRunsLeft(0);
        return;
      }
    } else if (fromError) {
      await frontendError();
      setFromError(false);
    }

    setShowCustomIdea(true);
    setMarketplaceIdeas(["", "", ""]);
    setMarketplaceDescription("");
    setSocialData({
      ...socialData,
      marketplace_description: "",
      marketplace_ideas: null,
    } as SocialData);
    setIdeaIndexLookup(null);
    setCurrentStep("Generating your alternative idea");
    currentStepKeyRef.current = "custom_try_again";
  };

  const restartMarketplaceCreation = (isRetry = false) => {
    setWaitStartTime(0); // disable long wait tracking for this interaction
    clearStepTimeout(); // Clear any pending timeouts and invalidate session
    setCustomIdea(null);
    setMarketplaceDescription("");
    setMarketplacePreview(null);
    setMarketplacePreviewDataId(null);
    setPaginatedListings(null);
    setMarketplacePreviewImageLoading(true); // prevents the launch modal from showing early
    setMarketplacePreviewImage(undefined);
    setSellerPreviewImage(undefined);
    setPayoutsPreviewImage(undefined);
    setListingPromoPreviewImages(undefined);
    setHasSelectedMarketplaceIdea(false);
    setImportKey(null);
    setRunIds([]);
    setGoogleRunId(null);
    setQueries(null);
    setFallbackTried(false);
    setMarketplaceDatas(null);
    // if already tried an X for Y, jump directly to the customize step on "Try Again"
    if (isRetry && ideaIndex === 3) {
      posthog.capture("custom_try_again", {
        loma_ai_event: true,
      });
      handleCustomTryAgain();
    } else if (isRetry) {
      posthog.capture("try_again", {
        loma_ai_event: true,
      });
      setCurrentStep("Generating alternative ideas");
      currentStepKeyRef.current = "custom_idea";
      setIdeaIndexLookup(null);
      setSocialData(null);
      setMarketplaceIdeas(null);
      // if they try again after selecting an idea, immediately generate 3 new ideas
      handleMainSubmit();
    } else {
      setCurrentStep("");
      currentStepKeyRef.current = "";
      setIdeaIndexLookup(null);
      setSocialData(null);
      setMarketplaceIdeas(null);
    }
    setIdeaIndex(-1);
  };

  const handleCustomizeClick = () => {
    posthog.capture("customize_clicked", {
      loma_ai_event: true,
      email: email,
      username: username,
      network: network,
      marketplace_ideas: marketplaceIdeas,
      marketplace_description: marketplaceDescription,
    });
    setCustomIdea(null);
    setMarketplaceDescription("");
    setMarketplacePreview(null);
    setMarketplacePreviewDataId(null);
    setIdeaIndex(-1);
    setShowCustomIdea(true);
  };

  const handleMainSubmit = async () => {
    console.log("handleMainSubmit", username, network, marketplaceDescription);

    // Track Submit event
    analytics.trackSubmit("main-form", {
      username,
      network,
      hasDescription: !!marketplaceDescription,
    });

    // Reset all state except username and network
    setMarketplaceDescription("");
    setMarketplacePreview(null);
    setMarketplacePreviewDataId(null);
    console.log("resetting idea index mainsubmit");
    setIdeaIndex(-1);
    setIdeaIndexLookup(null);
    setMarketplaceDatas(null);
    setCustomIdea(null);
    setPaginatedListings(null);
    setSocialData(null);
    setMarketplacePreviewImageLoading(true); // prevents the launch modal from showing early
    setMarketplacePreviewImage(undefined);
    setSellerPreviewImage(undefined);
    setPayoutsPreviewImage(undefined);
    setListingPromoPreviewImages(undefined);
    setMarketplaceIdeas(null);
    setHasSelectedMarketplaceIdea(false);
    setCurrentStep("");
    currentStepKeyRef.current = "";
    setImportKey(null);
    setRunIds([]);
    setGoogleRunId(null);
    setQueries(null);
    setFallbackTried(false);
    let currEmailRunId = emailRunId;
    if (!fromError && !isOverloaded) {
      const referralCode = localStorage.getItem("loma_referral_code");
      const newEmailRunId = await api.ai.createAIRunId(email, referralCode);
      currEmailRunId = newEmailRunId.id ?? null;
      if (newEmailRunId.id && newEmailRunId.runs_left) {
        setEmailRunId(newEmailRunId.id);
        setRunsLeft(newEmailRunId.runs_left);

        if (referralCode) {
          localStorage.removeItem("loma_referral_code");
        }

        posthog.capture("main_submit_create_run_id", {
          loma_ai_event: true,
          email: email,
          username: username,
          network: network,
          newEmailRunId: newEmailRunId.id,
        });
      } else {
        setRunsLeft(0);
        return;
      }
    } else if (fromError) {
      setFromError(false);
    }

    if (username && currEmailRunId) {
      setIsProcessing(true);
      setHasSelectedMarketplaceIdea(false);
      setCurrentStep("Analyzing social info");
      currentStepKeyRef.current = "social_data_json";
      setJobStartTime(performance.now());
      try {
        const runId = await api.ai.createInitialMarketplaceDataFromUsername(
          username,
          network,
          email,
          currEmailRunId
        );
        setRunIds([runId as string]);
        setImportKey("social_data_json");
        setIsProcessing(false);

        posthog.capture("main_submit_create_initial_marketplace_data", {
          loma_ai_event: true,
          email: email,
          username: username,
          network: network,
          runId: runId,
        });
      } catch (error) {
        const errorResponse = error?.response?.data;
        setIsProcessing(false);
        setCurrentStep("Experiencing high traffic");
        if (error?.status === 503) {
          toast({
            title: "Experiencing High Traffic",
            description:
              "Thank you for your interest. We are currently experiencing high traffic. We have saved your request and will notify you when our system is ready.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });

          posthog.capture("high_traffic", {
            loma_ai_event: true,
            email,
            network,
            username,
          });

          Sentry.captureException(new Error("High traffic"), {
            extra: {
              email,
              network,
              username,
            },
            tags: {
              logger_type: "loma-ai",
              feature: "loma-ai",
              action: "high-traffic",
            },
          });
        } else if (
          error?.status === 500 &&
          errorResponse?.error === "Email capture fallback was not successful"
        ) {
          toast({
            title: "Error",
            description: "Something went wrong. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });

          posthog.capture("email_capture_fallback_failed", {
            loma_ai_event: true,
            email,
            network,
            username,
          });

          Sentry.captureException(new Error("Email capture fallback failed"), {
            extra: {
              email,
              network,
              username,
            },
            tags: {
              logger_type: "loma-ai",
              feature: "loma-ai",
              action: "email-capture-fallback-failed",
            },
          });
        }
      }
    }
  };

  useEffect(() => {
    if (marketplacePreviewDataId && !socialData) {
      console.log(
        "useEffect to fetch marketplacePreviewData",
        marketplacePreviewDataId
      );
      const fetchMarketplacePreviewData = async () => {
        const response = await api.ai.getMarketplacePreview(
          marketplacePreviewDataId
        );
        setEmail(response.email);
        setIsEmailVerified(true);
        setUsername(response.social_data_json.username);
        setNetwork(response.social_data_json.network);
        setMarketplacePreview(response.marketplace_data);
        setSocialData(response.social_data_json);
        setIsPreviewSelected(response.is_selected);
        const generatedListings = {
          count: 10,
          next: "",
          previous: "",
          results: response.marketplace_data.listings as Listing[],
          page_number: 1,
          page_size: 10,
        } as PaginatedListingsResponse;
        setPaginatedListings(generatedListings);
        setMarketplacePreviewImage(response.marketplace_preview_image_url);
        setSellerPreviewImage(response.seller_preview_image_url);
        setPayoutsPreviewImage(response.payouts_preview_image_url);
        setListingPromoPreviewImages(response.listing_promo_preview_image_urls);
        setMarketplacePreviewImageLoading(false);
      };
      fetchMarketplacePreviewData();
    }
  }, [marketplacePreviewDataId]);

  useEffect(() => {
    const validateAccess = () => {
      // loads an existing preview data from the url directly
      const { preview_id } = router.query;
      setIsAuthorized(true);
      if (preview_id) {
        setMarketplacePreviewDataId(preview_id as string);
      }
      setIsLoading(false);
    };

    if (router.isReady) {
      validateAccess();
    }
  }, [router.isReady, router.query]);

  useEffect(() => {
    const handleVerificationToken = async () => {
      const { verification_token, social_username, social_network, ph_session_id, email } =
        router.query;

      if (
        verification_token &&
        typeof verification_token === "string" &&
        social_username &&
        social_network &&
        !isVerifyingByToken
      ) {
        setIsVerifyingByToken(true);
        try {
          setUsername(social_username as string);
          setNetwork(social_network as string);
          setHasSocialInput(true);
          setIsCustomAudience(social_network === "custom_audience");
          const result = await api.email.verifyEmailByToken(verification_token);
          setEmail(result.email);
          setIsEmailVerified(true);
          toast({
            title: "Email verified",
            description: "Your email has been verified successfully",
            status: "success",
            duration: 5000,
            isClosable: true,
          });

          if (ph_session_id && typeof ph_session_id === "string") {
            posthog.identify(ph_session_id, {
              email,
              username,
              network,
            });
          }

          posthog.capture("email_verified_by_token", {
            loma_ai_event: true,
            email,
            verification_token,
            social_username,
            social_network,
            ph_session_id,
          });
          await router.replace(router.pathname, undefined, { shallow: true });
          setIsAuthorized(true);

        } catch (error) {          
          toast({
            title: "Failed to verify email",
            description: "Please try again.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });

          Sentry.captureException(error, {
            extra: {
              email,
              verification_token,
            },
            tags: {
              logger_type: "loma-ai",
              feature: "loma-ai",
              action: "verify-email-by-token",
            },
          });

          posthog.capture("email_verification_by_token_failed", {
            loma_ai_event: true,
            email,
            verification_token,
            social_username,
            social_network,
          });
        } finally {
          setIsVerifyingByToken(false);
        }
      }
    };

    if (router.isReady) {
      handleVerificationToken();
    }
  }, [router.isReady, router.query, hasSocialInput, username]);

  const selectIdea = async (idea: string, index: number) => {
    setMarketplaceDescription(idea);
    setHasSelectedMarketplaceIdea(true);
    console.log("selecting idea", idea, index);
    setIdeaIndex(index);

    // If marketplace data isn't available (e.g., pre-generation timed out),
    // regenerate just this idea on-demand
    const ideaRunId = ideaIndexLookup?.[index];
    if (!marketplaceDatas || !ideaRunId || !marketplaceDatas[ideaRunId]) {
      console.log("marketplace data not ready for idea", index, "- regenerating on demand");
      setIsProcessing(true);
      setCurrentStep(`Creating ${socialData?.commerce_modality ?? "marketplace"} structure`);
      currentStepKeyRef.current = "marketplace_datas";
      setJobStartTime(performance.now());

      const runId = await api.ai.createInitialMarketplaceData({
        ideas_index: index,
        social_data_json: {
          ...socialData,
          marketplace_description: idea,
        } as SocialData,
        email_run_id: emailRunId,
        import_type: "custom_idea",
      });

      setIdeaIndexLookup({
        ...(ideaIndexLookup ?? {}),
        [index]: runId as string,
      });
      setRunIds([runId as string]);
      setImportKey("custom_idea");
    }
  };

  useEffect(() => {
    const generateQueries = async () => {
      console.log("generateQueries", ideaIndex);

      const mySelectedSocialData = {
        ...socialData,
        marketplace_description:
          socialData?.marketplace_ideas?.[ideaIndex] ?? "",
      } as SocialData;
      setSelectedSocialData(mySelectedSocialData);

      setIsProcessing(true);

      // generate the search queries
      setCurrentStep(
        marketplacePreview?.ui_text?.building ??
          `Building your ${marketplacePreview?.categories[0].name} category`
      );
      currentStepKeyRef.current = "queries";
      setListingsStartTime(performance.now());
      setJobStartTime(performance.now());
      const googleRunId = await api.ai.createListingsStep({
        step: "search_queries",
        social_data_json: mySelectedSocialData,
        marketplace_data: marketplacePreview,
        search_platform: "google",
        email_run_id: emailRunId,
        marketplace_preview_data_id: marketplacePreviewDataId,
      });
      const directRunId = await api.ai.createListingsStep({
        step: "search_queries",
        social_data_json: mySelectedSocialData,
        marketplace_data: marketplacePreview,
        search_platform: "", // let the backend determine the best 3P marketplace
        email_run_id: emailRunId,
      });
      setIsProcessing(false);
      setGoogleRunId(googleRunId as string);
      setRunIds([directRunId as string]);
      setImportKey("direct_queries");
      console.log(
        "generated queries with directRunId and googleRunId",
        directRunId,
        googleRunId
      );
    };
    if (
      !marketplacePreview ||
      !socialData ||
      paginatedListings ||
      ideaIndex === -1
    ) {
      console.log("skipping generateQueries", ideaIndex, marketplacePreview);
      return;
    }
    generateQueries();
  }, [marketplacePreview, socialData, paginatedListings, ideaIndex]);

  const fetchListings = async (queries: ListingsQueriesResponse) => {
    setCurrentStep(
      marketplacePreview?.ui_text?.fetching ??
        "Fetching sample inventory from the web"
    );
    currentStepKeyRef.current = "listings";
    setJobStartTime(performance.now());
    const listStep =
      queries.third_party_marketplace === "google"
        ? "google_listings"
        : "listings";

    // if we are running 10 Google queries, stop as soon as we get data from 8
    const queriesCount =
      queries.search_datas.length > 8 ? 8 : queries.search_datas.length;
    const promiseRunIds = await Promise.all(
      queries.search_datas.map((searchData) =>
        api.ai.createListingsStep({
          queries_count: queriesCount,
          step: listStep,
          third_party_marketplace: queries.third_party_marketplace,
          search_datas: [searchData],
          email_run_id: emailRunId,
        })
      )
    );
    const validRunIds = promiseRunIds.filter(
      (id): id is string => typeof id === "string"
    );
    setRunIds(validRunIds);
    setImportKey(listStep);
  };

  const listingsFallback = async (
    thirdPartyMarketplace: string,
    fallbackSearchDatas: unknown[]
  ) => {
    setJobStartTime(performance.now());
    const stepKey =
      thirdPartyMarketplace === "craigslist"
        ? "craigslist_fallback_listings"
        : "fallback_listings";
    const fallbackRunIds = await Promise.all(
      fallbackSearchDatas.map((fallbackSearchData) =>
        api.ai.createListingsStep({
          queries_count: fallbackSearchDatas.length,
          step: stepKey,
          third_party_marketplace: thirdPartyMarketplace,
          search_datas: [fallbackSearchData],
          email_run_id: emailRunId,
        })
      )
    );
    const validRunIds = fallbackRunIds.filter(
      (id): id is string => typeof id === "string"
    );
    setRunIds(validRunIds);
    setImportKey(stepKey);
    // prevent looping back to this step
    setFallbackTried(true);
  };

  const sortLomaListings = async (
    validatedLomaListings: ListingsLomaResponse
  ) => {
    setCurrentStep(
      marketplacePreview?.ui_text?.matching ??
        `Matching inventory to your ${marketplacePreview?.categories[0].name} category`
    );
    currentStepKeyRef.current = "sorted_listings";
    setJobStartTime(performance.now());
    const descriptionPrompt = `${marketplacePreview?.name ?? ""}, ${
      marketplacePreview?.description ??
      selectedSocialData?.marketplace_description ??
      ""
    }`;
    const sortedRunId = await api.ai.createListingsStep({
      step: "embeddings_filter",
      social_data_json: selectedSocialData,
      marketplace_data: marketplacePreview,
      description_prompt: descriptionPrompt,
      loma_listings: validatedLomaListings.loma_listings,
      marketplace_preview_data_id: marketplacePreviewDataId,
      email_run_id: emailRunId,
    });
    setRunIds([sortedRunId as string]);
    setImportKey("sorted_listings");
  };

  const createListingsData = async (
    sortedLomaListings: ListingsLomaResponse
  ) => {
    setCurrentStep(
      marketplacePreview?.ui_text?.converting ??
        `Converting inventory to ${
          socialData?.commerce_modality ?? "marketplace"
        } listings`
    );
    currentStepKeyRef.current = "listings_data";
    setJobStartTime(performance.now());
    const descriptionPrompt = `${marketplacePreview?.name ?? ""}, ${
      marketplacePreview?.description ??
      selectedSocialData?.marketplace_description ??
      ""
    }`;
    const listingsDataRunId = await api.ai.createListingsStep({
      step: "listing_dicts",
      third_party_marketplace: queries?.third_party_marketplace ?? "",
      marketplace_preview_data_id: marketplacePreviewDataId,
      commerce_modality: selectedSocialData?.commerce_modality ?? "marketplace",
      description_prompt: descriptionPrompt,
      loma_listings: sortedLomaListings.loma_listings,
      email_run_id: emailRunId,
    });
    setRunIds([listingsDataRunId as string]);
    setImportKey("listings_data");
  };

  useEffect(() => {
    const createPreviewImages = async () => {
      if (!marketplacePreviewDataId) return;
      setJobStartTime(performance.now());

      // Create the images for the three frames
      setMarketplacePreviewImageLoading(true);
      setSellerPreviewImageLoading(true);
      setPayoutsPreviewImageLoading(true);
      setListingPromoPreviewImagesLoading(true);
      try {
        const runId1 = await api.ai.getPreviewImage(
          "marketplace",
          marketplacePreviewDataId,
          emailRunId
        );
        const runId2 = await api.ai.getPreviewImage(
          "seller",
          marketplacePreviewDataId,
          emailRunId
        );
        const runId3 = await api.ai.getPreviewImage(
          "payouts",
          marketplacePreviewDataId,
          emailRunId
        );
        const runId4 = await api.ai.getListingPromoPreviewImage(
          marketplacePreviewDataId,
          emailRunId
        );
        setRunIds([
          runId1 as string,
          runId2 as string,
          runId3 as string,
          runId4 as string,
        ]);
        setImportKey("preview_images");
      } catch (error) {
        console.error("Error loading preview images:", error);
        
        posthog.capture("preview_images_creation_error", {
          loma_ai_event: true,
          email,
          network,
          username,
          marketplaceDescription,
          marketplacePreviewDataId,
          emailRunId,
        });

        Sentry.captureException(error instanceof Error ? error : new Error("Error loading preview images"), {
          extra: {
            email,
            network,
            username,
            marketplaceDescription,
            marketplacePreviewDataId,
            emailRunId,
          },
          tags: {
            logger_type: "loma-ai",
            feature: "loma-ai",
            action: "preview-images-creation-error",
          },
        });
        
        recoverError("at preview images");
        restartMarketplaceCreation();
        return;
      }
    };
    const { preview_id } = router.query;
    if (!preview_id && marketplacePreviewDataId && paginatedListings) {
      console.log("creating preview images with", marketplacePreviewDataId);
      createPreviewImages();
    }
  }, [marketplacePreviewDataId, paginatedListings]);

  useEffect(() => {
    setIsMounted(true);

    // Use a flag to prevent setting state after unmount
    let isMountedLocal = true;

    const loadLdrs = async () => {
      try {
        // Only register if not already registered to prevent multiple registrations
        if (!customElements.get("l-chaotic-orbit")) {
          const { chaoticOrbit } = await import("ldrs");
          if (isMountedLocal) {
            chaoticOrbit.register();
          }
        }
      } catch (error) {
        console.warn("Failed to load ldrs:", error);
      }
    };

    loadLdrs();

    // Cleanup function
    return () => {
      isMountedLocal = false;
    };
  }, []);

  useEffect(() => {
    if (marketplaceIdeas) {
      setVisibleIdeas([0]);
      setCurrentAnimatingIndex(0);
    }
  }, [marketplaceIdeas]);

  const launchWebApp = async () => {
    if (!marketplacePreviewDataId) return;

    // Track Make it Mine event
    analytics.trackMakeItMine(marketplacePreviewDataId);

    if (emailRunId) {
      setWaitStartTime(0); // disable long wait tracking for this interaction
      await api.ai.chooseAIRunId(email, marketplacePreviewDataId, emailRunId, upgradeRef.current || undefined);
      setIsPreviewSelected(true);
    }

    posthog.capture("make_it_mine", {
      loma_ai_event: true,
      email: email,
      username: username,
      network: network,
      marketplace_preview_data_id: marketplacePreviewDataId,
      marketplace_ideas: marketplaceIdeas,
      marketplace_description: marketplaceDescription,
    });

    onClose();
  };

  const validateYoutubeHandle = (handle: string) => {
    // Same regex logic as the backend in api/services/social_data_service.py resolve_username()
    const videoRegex = /^(?:https?:\/\/)?(?:www\.|m\.)?youtube\.com\/watch\?v=([^/?#]+)$/;
    if (videoRegex.test(handle)) {
      return "Invalid username format for YouTube. Video links are not supported. Provide your YouTube channel or username.";
    }
    const shortsRegex = /^(?:https?:\/\/)?(?:www\.|m\.)?youtube\.com\/shorts\/([^/?#]+)$/;
    if (shortsRegex.test(handle)) {
      return "Invalid username format for YouTube. Shorts links are not supported. Provide your YouTube channel or username.";
    }
    const liveRegex = /^(?:https?:\/\/)?(?:www\.|m\.)?youtube\.com\/live\/([^/?#]+)$/;
    if (liveRegex.test(handle)) {
      return "Invalid username format for YouTube. Live links are not supported. Provide your YouTube channel or username.";
    }
    const shortenedRegex = /^(?:https?:\/\/)?(?:www\.|m\.)?youtu\.be\/[^/?#]+$/;
    if (shortenedRegex.test(handle)) {
      return "Invalid username format for YouTube. Shortened links are not supported. Provide your YouTube channel or username.";
    }
    const youtubeHandleRegex = /^(?:https?:\/\/)?(?:www\.|m\.)?youtube\.com\/(@[^/?#]+|(?:user|c|channel)\/[^\/?#]+|[a-zA-Z0-9_.-]+)?\/?(?:\?[^#]*)?(?:#.*)?$/;
    if (!youtubeHandleRegex.test(handle)) {
      return "Invalid username format for YouTube. Provide your YouTube channel or username.";
    }
  };

  const handleSocialSubmit = () => {
    setNetworkValidationError(false);

    if (!username.trim()) {
      toast({
        title: "Input required",
        description: isCustomAudience
          ? "Please enter a description of your audience"
          : "Please enter your social handle or link",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    if (!isCustomAudience && username.match(EMAIL_REGEX_PATTERN)) {
      toast({
        title: "Invalid input",
        description: "Please enter your social media handle (like @yourname) or paste a link to your profile, not an email address.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    const genericPlatformNames = ['instagram', 'youtube', 'tiktok', 'twitter', 'x', 'facebook', 'discord', 'whatsapp', 'linkedin', 'snapchat'];
    const trimmedUsername = username.trim().toLowerCase();
    if (!isCustomAudience && genericPlatformNames.includes(trimmedUsername)) {
      toast({
        title: "Invalid handle",
        description: `Please enter your social media handle (like @yourname) or paste a link to your profile.`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    if (network === "youtube") {
      const youtubeError = validateYoutubeHandle(trimmedUsername);
      if (youtubeError) {
        toast({
          title: "Invalid handle",
          description: youtubeError,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom",
        });
        return;
      }
    }

    if (!isCustomAudience && !network.trim()) {
      setNetworkValidationError(true);
      toast({
        title: "Social network required",
        description: formatErrorWithSupport("Please select a social network platform"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    if (isCustomAudience) {
      setNetwork("custom_audience");
    }

    setHasSocialInput(true);

    posthog.capture("social_input", {
      loma_ai_event: true,
      email: email,
      username: username,
      network: network,
    });
  };

  const handleEmailSubmit = async () => {
    if (!email || !email.match(EMAIL_REGEX_PATTERN)) {
      toast({
        title: "Invalid email",
        description: "Please enter your email address",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSendingCode(true);
    try {
      // Track Send Code event
      analytics.trackSendCode(email);

      const sessionId = posthog.get_session_id();

      posthog.capture("send_verification_code", {
        loma_ai_event: true,
        email: email,
        username: username,
        network: network,
      });

      posthog.identify(sessionId, {
        email: email,
        username: username,
        network: network,
      });

      const result = (await api.email.sendVerificationCode(
        email,
        username,
        network,
        sessionId
      )) as EmailVerificationResponse;

      // Check if email is allowlisted
      if (result.is_verified) {
        setIsEmailVerified(true);
        toast({
          title: "Welcome!",
          description: "Your email has been verified.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        return;
      }

      toast({
        title: "Verification code sent",
        description: "Check your email for the code.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setIsVerifying(true);
    } catch (error: any) {
      const errorResponse = error?.response?.data;

      // Handle 403 Forbidden specifically while in private beta
      if (error?.status === 403) {
        toast({
          title: "Private Beta",
          description:
            errorResponse?.error ||
            "We are currently in private beta and only accepting a limited number of users. Please request access at contact@withloma.com.",
          status: "info",
          duration: 8000,
          isClosable: true,
        });
      } else {        
        toast({
          title: "Verification code not sent",
          description:
            "There was a problem sending the verification code. Please try again.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        
        Sentry.captureException(error, {
          extra: {
            email,
            errorResponse,
          },
          tags: {
            logger_type: "loma-ai",
            feature: "loma-ai",
            action: "send-verification-code",
          },
        });

        posthog.capture("verification_code_send_failed", {
          loma_ai_event: true,
          email,
          network,
          username,
          errorStatus: error?.status,
          errorResponse,
        });
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerificationSubmit = async () => {
    if (!verificationCode) {
      toast({
        title: "Verification code required",
        description: "Please enter the verification code",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsVerifying(true);
    try {
      const result = await api.email.verifyEmail(email, verificationCode);
      setRunsLeft(result.runs_left);
      setIsEmailVerified(true);
      toast({
        title: "Email verified",
        description: "Your email has been verified successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      posthog.capture("email_verified", {
        loma_ai_event: true,
        email: email,
        verification_code: verificationCode,
      });
    } catch (error) {      
      toast({
        title: "Invalid verification code",
        description: "Please check your code and try again",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });

      posthog.capture("email_verification_by_code_failed", {
        loma_ai_event: true,
        email,
        verification_code: verificationCode,
      });
      
    } finally {
      setIsVerifying(false);
    }
  };

  // Automatically show claim your marketplace modal when conditions are met
  useEffect(() => {
    if (isDoneAndShowing && !isPreviewSelected) {
      // track how long before the user clicks "Make it mine" or leaves the page
      setWaitStartTime(performance.now());
      onOpen();
    }
  }, [isDoneAndShowing, isPreviewSelected]);

  // Track platform selection
  const handlePlatformSelect = (platform: string) => {
    analytics.trackPlatformSelect(platform);
    setNetwork(platform);
    setNetworkValidationError(false);
  };

  const useCustomIdea = (customIdea: string) => {
    setShowCustomIdea(false);
    setMarketplaceDescription(customIdea);
    if (socialData) {
      setSocialData({
        ...socialData,
        marketplace_description: customIdea,
        marketplace_ideas: [
          ...(socialData?.marketplace_ideas || []),
          customIdea,
        ],
      });
    }
    setCustomIdea(customIdea);

    posthog.capture("custom_idea_submitted", {
      loma_ai_event: true,
      idea_description: customIdea,
      marketplace_ideas: marketplaceIdeas,
      marketplace_description: marketplaceDescription,
      email: email,
      username: username,
      network: network,
    });
  };

  if (!router.isReady || isLoading) {
    return null;
  }

  if (!isAuthorized) {
    return <UnauthorizedView />;
  }

  return (
    <>
      <NextSeo title="Loma AI" canonical="https://www.loma.ai" />
      <Head>
        <link
          rel="icon"
          href="https://loma-files.s3.us-west-1.amazonaws.com/favicons/favicon.ico"
        />
      </Head>
      <BackgroundHills />
      <Header />

      <Container maxW="4xl" mt={{ base: 4, md: 8 }}>
        <HeroSection />

        {/* Main Content Area */}
        <VStack spacing={{ base: 8, md: 12 }} align="stretch">
          {/* Show LoadingExperience when processing marketplace ideas or beyond */}
          {(marketplaceIdeas && !hasSelectedMarketplaceIdea) ||
          (hasSelectedMarketplaceIdea && (!marketplacePreview || !paginatedListings)) ? (
            <Box width="100%">
              {/* Marketplace Ideas Section */}
              {marketplaceIdeas &&
                marketplaceIdeas.length > 1 &&
                !hasSelectedMarketplaceIdea && (
                  <MarketplaceIdeas
                    marketplaceIdeas={marketplaceIdeas}
                    marketplaceDescription={marketplaceDescription}
                    visibleIdeas={visibleIdeas}
                    currentAnimatingIndex={currentAnimatingIndex}
                    onDescriptionChange={(value) => {
                      setMarketplaceDescription(value);
                      
                      posthog.capture("idea_selected", {
                        loma_ai_event: true,
                        idea_description: value,
                        marketplace_ideas: marketplaceIdeas,
                        marketplace_description: marketplaceDescription,
                        email: email,
                        username: username,
                        network: network,
                      });
                    }}
                    onCreateClick={(description) => {
                      const index = marketplaceIdeas.findIndex(
                        (idea) => idea === description
                      );
                      selectIdea(description, index);
                      // Track Create event when an idea is selected
                      analytics.trackCreate({
                        idea_index: index,
                        idea_description: description,
                      });

                      posthog.capture("idea_submitted", {
                        loma_ai_event: true,
                        idea_description: description,
                        idea_index: index,
                        marketplace_ideas: marketplaceIdeas,
                        marketplace_description: marketplaceDescription,
                        email: email,
                        username: username,
                        network: network,
                      });
                    }}
                    onTryAgainClick={handleMainSubmit}
                    onCustomizeClick={handleCustomizeClick}
                    onAnimationComplete={(index) => {
                      setCurrentAnimatingIndex(index + 1);
                      setVisibleIdeas((prev) => [...prev, index + 1]);
                    }}
                    showCustomIdea={showCustomIdea}
                    useCustomIdea={useCustomIdea}
                  />
                )}

              {/* Loader for when they've selected an idea but it's not ready yet */}
              {hasSelectedMarketplaceIdea &&
                (!marketplacePreview || !paginatedListings) && (
                  <LoadingExperience currentStep={currentStep} currentStepKey={currentStepKeyRef.current} />
                )}
            </Box>
          ) : isDone &&
            marketplacePreview &&
            socialData &&
            paginatedListings &&
            paginatedListings.results ? (
            /* Show Phone Frames when results are ready */
            <Box width="100%" maxW="100%">
              <MarketplacePreviewComponent
                marketplacePreviewImageLoading={marketplacePreviewImageLoading}
                sellerPreviewImageLoading={sellerPreviewImageLoading}
                payoutsPreviewImageLoading={payoutsPreviewImageLoading}
                listingPromoPreviewImagesLoading={listingPromoPreviewImagesLoading}
                marketplacePreviewImage={marketplacePreviewImage}
                sellerPreviewImage={sellerPreviewImage}
                payoutsPreviewImage={payoutsPreviewImage}
                listingPromoPreviewImages={listingPromoPreviewImages}
                username={username}
                marketplacePreview={marketplacePreview}
                socialData={socialData}
                showFallbackInstagram={showFallbackInstagram}
                listings={paginatedListings.results}
              />
              
              {/* Action Buttons */}
              <VStack spacing={4} mt={8} maxW="400px" mx="auto">
                {isDoneAndShowing ? (
                  isPreviewSelected ? (
                    <Box bg="white" boxShadow="md" borderRadius="md" p={4} textAlign="center">
                      <Heading size="md" mb={2} color="gray.800">
                        Congratulations! 🎉
                      </Heading>
                      <Text color="gray.800" fontSize="sm">
                        You're ready for customization and launch! Our team
                        will reach out via email shortly to help you get started.
                      </Text>
                    </Box>
                  ) : (
                    <>
                      {!isOpen && (
                        <>
                          <MotionBox
                            width="full"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <Button
                              size="md"
                              width="full"
                              height={{ base: "48px", md: "56px" }}
                              bgGradient={`linear(135deg, #7BC9CC 0%, #357E81 100%)`}
                              color="white"
                              borderRadius={{ base: "12px", md: "16px" }}
                              fontSize={{ base: "md", md: "lg" }}
                              fontWeight="600"
                              onClick={launchWebApp}
                              border="0px"
                              _hover={{
                                bgGradient: `linear(135deg,rgb(114, 188, 191) 0%,rgb(43, 105, 107) 100%)`,
                                transform: "translateY(-2px)",
                                boxShadow: `0 12px 24px -4px rgb(114, 188, 191)40`
                              }}
                            >
                              Make it mine! 🎉
                            </Button>
                          </MotionBox>
                          <MotionBox
                            width="full"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <Button
                              size="md"
                              width="full"
                              height={{ base: "48px", md: "56px" }}
                              variant="outline"
                              bg="white"
                              color="gray.800"
                              borderRadius={{ base: "12px", md: "16px" }}
                              fontSize={{ base: "md", md: "lg" }}
                              boxShadow={`inset 0 0 0 1px gray.800`}
                              onClick={() => restartMarketplaceCreation(true)}
                              _hover={{
                                bg: `gray.80020`,
                                boxShadow: `inset 0 0 0 1px gray.800`
                              }}
                            >
                              Try Again
                            </Button>
                          </MotionBox>
                        </>
                      )}
                    </>
                  )
                ) : null}
              </VStack>
            </Box>
          ) : (
            /* Show EmojiList and Input Cards */
            <>
              <EmojiList />
              
              {/* Centered Input Card */}
              <Flex justify="center" width="100%">
                <MotionBox
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  position="relative"
                  // maxW="500px"
                  width="100%"
                >
                  {/* Subtle glow effect */}
                  <Box
                    position="absolute"
                    top="-2px"
                    left="-2px"
                    right="-2px"
                    bottom="-2px"
                    bgGradient={`linear(135deg, #7BC9CC 10, #357E81 08)`}
                    borderRadius="24px"
                    opacity={0.3}
                    filter="blur(12px)"
                  />

                  <Box
                    bg="white"
                    borderRadius={{ base: "16px", md: "20px" }}
                    border="1px"
                    borderColor="gray.200"
                    boxShadow={`0 32px 64px -12px rgba(0,0,0,0.15), 0 8px 32px -8px rgba(0,0,0,0.1)`}
                    p={{ base: 5, md: 8 }}
                    position="relative"
                  >
                    <VStack spacing={4} align="stretch">
                        {/* Processing State - Show inputs and loading button */}
                        {currentStep && !marketplaceIdeas ? (
                          currentStep === "Analyzing social info" ? (
                            <SocialAnalysisLoader 
                              network={network} 
                              username={username}
                              networkIcons={networkIcons}
                            />
                          ) : (
                          <ProcessingState
                            isCustomAudience={isCustomAudience}
                            username={username}
                            network={network}
                            networkIcons={networkIcons}
                            isMounted={isMounted}
                          />
                        )
                      ) : runsLeft === 0 ? (
                        /* Daily Limit Reached State */
                        <DailyInputReached />
                      ) : !hasSocialInput ? (
                        /* Social Media Input Section */
                        <VStack spacing={4} align="stretch">
                          {/* Referral Banner */}
                          {referralBannerState.show && (
                            <ReferralBanner
                              isValid={referralBannerState.isValid}
                              inviterEmail={referralBannerState.inviterEmail}
                            />
                          )}
                          
                          <FormControl id="username">
                            <Text fontSize="sm" color="gray.600" mb={3} textAlign="center">
                              {isCustomAudience ? "Describe your audience" : ""}
                            </Text>
                            
                            <Flex 
                              width="full" 
                              gap={{ base: 3, md: 4 }}
                              direction={{ base: "column", md: "row" }}
                            >
                              {!isCustomAudience && (
                                <Menu>
                                  <MenuButton
                                    as={Button}
                                    height={{ base: "48px", md: "56px" }}
                                    width={{ base: "full", md: "240px" }}
                                    minW={{ base: "full", md: "240px" }}
                                    maxW={{ base: "full", md: "240px" }}
                                    bg="gray.50"
                                    color="gray.800"
                                    borderColor={networkValidationError ? "red.300" : "gray.300"}
                                    borderWidth="1px"
                                    borderRadius={{ base: "12px", md: "16px" }}
                                    transition="all 0.3s ease"
                                    justifyContent="space-between"
                                    rightIcon={<FaChevronDown />}
                                    _hover={{
                                      borderColor: networkValidationError ? "red.400" : "gray.400",
                                      bg: "gray.100"
                                    }}
                                    _expanded={{
                                      borderColor: networkValidationError ? "red.400" : "blue.500",
                                      boxShadow: networkValidationError 
                                        ? `0 0 0 3px rgba(255, 0, 0, 0.2)` 
                                        : `0 0 0 3px rgba(59, 130, 246, 0.15)`
                                    }}
                                  >
                                    {network ? (
                                      <Flex align="center" overflow="hidden">
                                        <Icon as={networkIcons[network]} boxSize={5} flexShrink={0} color="gray.600" />
                                        <Text ml={3} fontWeight="500" isTruncated color="gray.800">
                                          {networkDisplayNames[network]}
                                        </Text>
                                      </Flex>
                                    ) : (
                                      <Text 
                                        color={networkValidationError ? "red.400" : "gray.900"}
                                        fontWeight="400"
                                        textAlign="left"
                                      >
                                        Select Social Network
                                      </Text>
                                    )}
                                  </MenuButton>
                                  <Portal>
                                    <MenuList
                                      bg="white"
                                      borderColor="gray.200"
                                      borderRadius="12px"
                                      boxShadow="xl"
                                      py={2}
                                      minW="240px"
                                    >
                                      {Object.entries(networkIcons).map(([key, IconComponent]) => {
                                        const isComingSoon = networkDisplayNames[key] === "Coming Soon";
                                        return (
                                          <MenuItem
                                            key={key}
                                            onClick={() => !isComingSoon && handlePlatformSelect(key)}
                                            isDisabled={isComingSoon}
                                            opacity={isComingSoon ? 0.5 : 1}
                                            borderRadius="8px"
                                            bg="white"
                                            _hover={{
                                              bg: isComingSoon ? "transparent" : "gray.50"
                                            }}
                                          >
                                            <Flex align="center" width="100%">
                                              <Icon as={IconComponent} boxSize={5} color="gray.600" />
                                              <Text ml={3} fontWeight="500" color="gray.800">
                                                {networkDisplayNames[key] || key}
                                              </Text>
                                            </Flex>
                                          </MenuItem>
                                        );
                                      })}
                                    </MenuList>
                                  </Portal>
                                </Menu>
                              )}

                              <InputGroup size={{ base: "md", md: "lg" }} flex={1}>
                                <Input
                                  type="text"
                                  value={username}
                                  onChange={(e) => setUsername(e.target.value)}
                                  placeholder={
                                    isCustomAudience
                                      ? typewriterPlaceholder
                                      : placeholder
                                  }
                                  bg="gray.50"
                                  color="gray.800"
                                  _placeholder={{ color: "gray.700" }}
                                  borderColor="gray.300"
                                  borderWidth="1px"
                                  borderRadius={{ base: "12px", md: "16px" }}
                                  fontSize="16px"
                                  height={{ base: "48px", md: "56px" }}
                                  px={{ base: 4, md: 6 }}
                                  transition="all 0.3s ease"
                                  _hover={{
                                    borderColor: "gray.400",
                                    bg: "gray.100"
                                  }}
                                  _focus={{
                                    borderColor: "blue.500",
                                    boxShadow: `0 0 0 3px rgba(59, 130, 246, 0.15)`,
                                    bg: "white"
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleSocialSubmit();
                                    }
                                  }}
                                />
                              </InputGroup>
                            </Flex>
                          </FormControl>

                          <MotionBox
                            width="full"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <Button
                              size={{ base: "md", md: "lg" }}
                              width="full"
                              height={{ base: "48px", md: "56px" }}
                              bgGradient={`linear(135deg, #7BC9CC 0%, #357E81 100%)`}
                              color="white"
                              borderRadius={{ base: "12px", md: "16px" }}
                              fontSize={{ base: "md", md: "lg" }}
                              fontWeight="600"
                              _disabled={{ opacity: 1, cursor: "not-allowed" }}
                              onClick={handleSocialSubmit}
                              isDisabled={!username.trim()}
                              _hover={{
                                bgGradient: `linear(135deg, #A3DEE0 0%, #2D6E71 100%)`,
                                transform: "translateY(-2px)",
                                boxShadow: `0 12px 24px -4px rgba(0,0,0,0.25)`
                              }}
                            >
                              {isCustomAudience ? "Build Custom Platform ✨" : "Build from Social Media ⚡"}
                            </Button>
                          </MotionBox>

                          <Text color="gray.600" fontSize="sm" textAlign="center">
                            {isCustomAudience ? (
                              <Text
                                as="u"
                                cursor="pointer"
                                color="blue.600"
                                fontWeight="500"
                                onClick={() => {
                                  setIsCustomAudience(false);
                                  setUsername("");
                                  setNetwork("instagram");
                                }}
                              >
                                ← Back to social media
                              </Text>
                            ) : (
                              <>
                                Or{" "}
                                <Text
                                  as="u"
                                  cursor="pointer"
                                  color="blue.600"
                                  fontWeight="500"
                                  onClick={() => {
                                    setIsCustomAudience(true);
                                    setUsername("");
                                    setNetwork("custom_audience");
                                  }}
                                >
                                  describe your audience
                                </Text>
                              </>
                            )}
                          </Text>
                        </VStack>
                      ) : !isEmailVerified ? (
                        /* Email Verification Section */
                        <VStack spacing={4} align="stretch">
                          <Text fontSize="md" color="gray.700" textAlign="center" mb={2}>
                            We only let real people use our AI tools, so enter
                            your email below to verify you're human.
                          </Text>
                          
                          <Flex width="full" align="center" gap={3}>
                            {isVerifying ? (
                              <FormControl id="verification-code">
                                <Input
                                  type="text"
                                  value={verificationCode}
                                  onChange={(e) => setVerificationCode(e.target.value)}
                                  placeholder="Enter verification code"
                                  bg="gray.50"
                                  color="gray.800"
                                  _placeholder={{ color: "gray.500" }}
                                  borderColor="gray.300"
                                  borderWidth="1px"
                                  borderRadius={{ base: "12px", md: "16px" }}
                                  fontSize="16px"
                                  height={{ base: "48px", md: "56px" }}
                                  px={{ base: 4, md: 6 }}
                                  transition="all 0.3s ease"
                                  _hover={{
                                    borderColor: "gray.400",
                                    bg: "gray.100"
                                  }}
                                  _focus={{
                                    borderColor: "blue.500",
                                    boxShadow: `0 0 0 3px rgba(59, 130, 246, 0.15)`,
                                    bg: "white"
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleVerificationSubmit();
                                    }
                                  }}
                                />
                              </FormControl>
                            ) : (
                              <FormControl
                                id="email"
                                isInvalid={
                                  isEmailTouched &&
                                  email !== "" &&
                                  !email.match(EMAIL_REGEX_PATTERN)
                                }
                              >
                                <InputGroup
                                  bg="gray.50"
                                  borderColor="gray.300"
                                  borderWidth="1px"
                                  borderRadius={{ base: "12px", md: "16px" }}
                                  transition="all 0.3s ease"
                                  _hover={{
                                    borderColor: "gray.400",
                                    bg: "gray.100"
                                  }}
                                  _focusWithin={{
                                    borderColor: "blue.500",
                                    boxShadow: `0 0 0 3px rgba(59, 130, 246, 0.15)`,
                                    bg: "white"
                                  }}
                                  height={{ base: "48px", md: "56px" }}
                                >
                                  <InputLeftElement
                                    pointerEvents="none"
                                    h="100%"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <Icon
                                      as={FaRegEnvelope}
                                      color="gray.500"
                                      boxSize={4}
                                    />
                                  </InputLeftElement>
                                  <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onBlur={() => setIsEmailTouched(true)}
                                    placeholder="Enter your email"
                                    border="none"
                                    borderRadius={{ base: "12px", md: "16px" }}
                                    pl={10}
                                    fontSize="16px"
                                    height="100%"
                                    color="gray.800"
                                    _placeholder={{ color: "gray.500" }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        handleEmailSubmit();
                                      }
                                    }}
                                  />
                                </InputGroup>
                                {isEmailTouched &&
                                  email !== "" &&
                                  !email.match(EMAIL_REGEX_PATTERN) && (
                                    <FormErrorMessage mt={2}>
                                      Please enter a valid email address
                                    </FormErrorMessage>
                                  )}
                              </FormControl>
                            )}
                            <Button
                              size={{ base: "md", md: "lg" }}
                              height={{ base: "48px", md: "56px" }}
                              px={6}
                              bgGradient={`linear(135deg, #7BC9CC 0%, #357E81 100%)`}
                              color="white"
                              borderRadius={{ base: "12px", md: "16px" }}
                              fontWeight="600"
                              onClick={
                                isVerifying
                                  ? handleVerificationSubmit
                                  : handleEmailSubmit
                              }
                              isLoading={isSendingCode}
                              spinner={
                                isMounted ? (
                                  // @ts-ignore
                                  <l-chaotic-orbit
                                    size="24"
                                    speed="1"
                                    color="white"
                                  />
                                ) : undefined
                              }
                              isDisabled={
                                isVerifyingByToken ||
                                !email.match(EMAIL_REGEX_PATTERN)
                              }
                              _hover={{
                                bgGradient: `linear(135deg, #7BC9CC 0%, #357E81 100%)`,
                                transform: "translateY(-2px)",
                                boxShadow: `0 12px 24px -4px ${LOMA_GREEN}40`
                              }}
                              _loading={{
                                bgGradient: `linear(135deg, #7BC9CC 0%, #357E81 100%)`,
                                color: "white",
                                _hover: {
                                  bgGradient: `linear(135deg, #7BC9CC 0%, #357E81 100%)`,
                                }
                              }}
                            >
                              {isVerifying ? "Verify" : getStartedButtonText}
                            </Button>
                          </Flex>
                          {isVerifyingByToken && (
                            <Text fontSize="sm" color="gray.600" textAlign="center">
                              Verifying your email...
                            </Text>
                          )}
                        </VStack>
                      ) : isEmailVerified &&
                        email &&
                        network &&
                        username &&
                        !currentStep ? (
                        /* Ready to Build State */
                        <ReadyToBuild handleMainSubmit={handleMainSubmit} />
                      ) : null}
                    </VStack>
                  </Box>
                </MotionBox>
              </Flex>
            </>
          )}
        </VStack>
      </Container>

      <ExampleCarousels />
      <ComparisonTable />
      <LaunchModal isOpen={isOpen} onClose={onClose} onLaunch={launchWebApp} />
    </>
  );
};

LomaAI.PageLayout = SimpleLayout;

export default LomaAI;